import React, { useState } from "react";
import { MessageSquare, Sparkles, Send, RefreshCw, ArrowRight, Compass, SearchCheck, UserCheck, Bookmark, Search, AlertTriangle } from "lucide-react";
import { AI_FEATURES_ENABLED } from "../../config/featureFlags";

interface CareerAdvisorToolProps {
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
  savedEmployers?: any[];
  userPreferences?: any;
  onNavigateToTool?: (toolId: string) => void;
  theme?: "light" | "dark";
  providerHealth?: { status: string; outageReason?: string | null };
  onProviderError?: (err: any) => void;
}

export default function CareerAdvisorTool({
  apiFetch,
  savedEmployers = [],
  userPreferences,
  onNavigateToTool,
  theme = "light",
  providerHealth,
  onProviderError
}: CareerAdvisorToolProps) {
  const isDark = theme === "dark";
  const isCircuitOpen = providerHealth?.status === "outage";

  const [messages, setMessages] = useState<
    { role: "user" | "advisor"; data?: any; text?: string }[]
  >([
    {
      role: "advisor",
      text: AI_FEATURES_ENABLED
        ? "Hello! I am your AI Career Advisor. I track your job search activity, saved employers, and pipeline. Ask me what to do next or how to optimize your job search today."
        : "Hello! I am your Career Advisor. I track your job search activity, saved employers, and pipeline. Ask me what to do next or how to optimize your job search today."
    }
  ]);

  const [inputQuery, setInputQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSendMessage = async (e?: React.FormEvent, customPrompt?: string) => {
    if (e) e.preventDefault();
    if (isLoading || isCircuitOpen) return;

    if (!AI_FEATURES_ENABLED) {
      setError("AI features are currently disabled.");
      return;
    }

    const queryToSend = customPrompt || inputQuery;
    if (!queryToSend.trim()) return;

    setIsLoading(true);
    setError(null);

    const newHistory = [...messages, { role: "user" as const, text: queryToSend }];
    setMessages(newHistory);
    if (!customPrompt) setInputQuery("");

    try {
      const res = await apiFetch("/api/ai/career-advisor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userQuery: queryToSend,
          accountContext: {
            savedCount: savedEmployers.length,
            recentSearches: userPreferences?.recentSearches || [],
            topSavedEmployers: savedEmployers.slice(0, 5).map((e) => e.name),
            userProfile: userPreferences
          },
          chatHistory: newHistory.map((m) => ({
            role: m.role === "user" ? "user" : "model",
            text: m.text || m.data?.fullResponseText || ""
          }))
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        if (data.providerError || res.status === 503 || res.status === 502) {
          if (onProviderError) onProviderError(data.providerError || data);
        }
        throw new Error(data.error || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "advisor",
          data: data.data,
          text: data.data.fullResponseText
        }
      ]);
    } catch (err: any) {
      console.error("Career Advisor error:", err);
      setError(err.message || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
    } finally {
      setIsLoading(false);
    }
  };

  const getToolIcon = (toolId: string) => {
    switch (toolId) {
      case "job_search_agent":
        return Compass;
      case "opportunity_analyst":
        return SearchCheck;
      case "outreach_assistant":
        return Send;
      case "interview_coach":
        return UserCheck;
      case "saved":
        return Bookmark;
      case "search":
      default:
        return Search;
    }
  };

  return (
    <div className="space-y-6 text-left">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 md:p-6 shadow-xs">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">AI Career Advisor</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Conversational strategist providing situation analysis, primary issues, and direct action recommendations.
            </p>
          </div>
        </div>

        {/* Quick Question Chips */}
        <div className="flex flex-wrap gap-2 mt-4">
          {[
            "What should I do today?",
            "Which saved opportunity should I focus on?",
            "Why am I not finding useful jobs?",
            "Should I broaden my search radius?"
          ].map((promptText, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSendMessage(undefined, promptText)}
              disabled={isLoading}
              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950 text-slate-700 dark:text-slate-300 font-bold text-xs rounded-xl transition cursor-pointer border border-slate-200 dark:border-slate-700"
            >
              {promptText}
            </button>
          ))}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="space-y-4">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex flex-col ${
              msg.role === "user" ? "items-end" : "items-start"
            }`}
          >
            {msg.role === "user" ? (
              <div className="bg-indigo-600 text-white rounded-2xl px-4 py-3 max-w-xl text-xs font-semibold shadow-xs">
                {msg.text}
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 max-w-2xl text-xs text-slate-800 dark:text-slate-200 shadow-xs space-y-3">
                {msg.data ? (
                  <div className="space-y-3">
                    {/* Structured response sections */}
                    <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-100 dark:border-slate-800 space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
                        1. Current Situation
                      </span>
                      <p className="font-medium">{msg.data.currentSituation}</p>
                    </div>

                    <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-900/50 space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 dark:text-amber-400 block">
                        2. Most Important Issue
                      </span>
                      <p className="font-extrabold text-amber-900 dark:text-amber-200">{msg.data.mostImportantIssue}</p>
                    </div>

                    <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl border border-indigo-200 dark:border-indigo-800 space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400 block">
                        3. Recommended Next Action & Reason
                      </span>
                      <p className="font-extrabold text-indigo-950 dark:text-indigo-200">{msg.data.recommendedNextAction}</p>
                      <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-1">{msg.data.reason}</p>
                    </div>

                    {/* Direct Action Link / Button */}
                    {msg.data.suggestedToolLink && onNavigateToTool && (
                      <div className="pt-2">
                        <button
                          type="button"
                          onClick={() => onNavigateToTool(msg.data.suggestedToolLink.toolId)}
                          className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold text-xs rounded-xl shadow-xs transition flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <Sparkles className="w-4 h-4 text-amber-300" />
                          <span>{msg.data.suggestedToolLink.label || "Open Recommended Tool"}</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="leading-relaxed">{msg.text}</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-800 rounded-2xl flex items-start gap-3 text-xs text-amber-950 dark:text-amber-100 shadow-xs">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1 min-w-0 flex-1">
            <p className="font-bold text-amber-900 dark:text-amber-200">AI Service Notice</p>
            <p className="leading-relaxed whitespace-pre-line">{error}</p>
          </div>
        </div>
      )}

      {/* Input box */}
      <form onSubmit={handleSendMessage} className="flex gap-2">
        <input
          type="text"
          value={inputQuery}
          disabled={isLoading || isCircuitOpen}
          onChange={(e) => setInputQuery(e.target.value)}
          placeholder={isCircuitOpen ? "AI tools are temporarily unavailable..." : "Ask the AI Career Advisor a question about your job search..."}
          className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={isLoading || isCircuitOpen || !inputQuery.trim()}
          className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-400 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-extrabold text-xs rounded-xl shadow-xs transition flex items-center gap-2 cursor-pointer shrink-0"
        >
          {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          <span>{isCircuitOpen ? "Unavailable" : "Ask Advisor"}</span>
        </button>
      </form>
    </div>
  );
}
